import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { risapi } from '../../api/risapi'; 
import { getAxiosConfig } from '../../api/risapi';
import { 
    BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
    PieChart, Pie, Cell, Legend 
} from 'recharts';

// --- Helper Components ---
const formatCurrency = (val) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(val);
const formatCompactNumber = (number) => new Intl.NumberFormat('en-IN', { notation: "compact", compactDisplay: "short" }).format(number);

// --- 1. Admin Bar Chart (Sales Trend - Existing) ---
const AdminBarChart = ({ data }) => {
    if (!data || data.length === 0) return <div className="text-muted text-center py-5">No data available</div>;

    // Map 'sales' to 'value' for the existing chart logic
    const chartData = data.map(item => ({
        ...item,
        value: parseFloat(item.sales) || 0
    }));

    return (
        <div style={{ width: '100%', height: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 20, right: 10, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e9ecef" />
                    <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: '#6c757d', fontSize: 11 }} dy={10} interval="preserveStartEnd" />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: '#6c757d', fontSize: 11 }} tickFormatter={formatCompactNumber} />
                    <Tooltip 
                        cursor={{ fill: 'rgba(0,0,0,0.03)' }}
                        content={({ active, payload, label }) => {
                            if (active && payload && payload.length) {
                                return (
                                    <div className="bg-dark text-white px-3 py-2 rounded shadow-sm" style={{ fontSize: '0.85rem' }}>
                                        <div className="mb-1 opacity-75 small">{label}</div>
                                        <div className="fw-bold fs-6">{formatCurrency(payload[0].value)}</div>
                                    </div>
                                );
                            }
                            return null;
                        }}
                    />
                    <Bar dataKey="value" fill="#0F626A" radius={[4, 4, 0, 0]} maxBarSize={50} activeBar={{ fill: '#0a4b52' }} />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
};

// --- 2. Admin Pie Chart (Revenue Share - Existing) ---
const AdminPieChart = ({ data, nameKey = "name" }) => {
    const COLORS = ['#0F626A', '#15838d', '#1aa5b1', '#20c7d5', '#82e0e9', '#bdf0f5'];
    if (!data || data.length === 0) return <div className="text-muted text-center py-5">No data available</div>;

    // Sort by value (revenue)
    let processedData = data.map(item => ({
        name: item[nameKey],
        value: parseFloat(item.value) || 0
    })).sort((a, b) => b.value - a.value).slice(0, 6); // Top 6

    return (
        <div style={{ width: '100%', height: 320 }}>
            <ResponsiveContainer>
                <PieChart>
                    <Pie data={processedData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={2} dataKey="value">
                        {processedData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} stroke="none" />
                        ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} contentStyle={{ backgroundColor: '#212529', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '0.85rem' }} itemStyle={{ color: '#fff' }} />
                    <Legend verticalAlign="bottom" height={36} iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '0.75rem', paddingTop: '10px' }} />
                </PieChart>
            </ResponsiveContainer>
        </div>
    );
};

// --- 3. NEW: Area Chart (Revenue vs Orders) ---
const AdminAreaChart = ({ data }) => {
    if (!data || data.length === 0) return <div className="text-muted text-center py-5">No data available</div>;

    const chartData = data.map(item => ({
        ...item,
        sales: parseFloat(item.sales) || 0,
        orders: parseInt(item.orders) || 0
    }));

    return (
        <div style={{ width: '100%', height: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 10, right: 0, left: 0, bottom: 0 }}>
                    <defs>
                        <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#0F626A" stopOpacity={0.1}/>
                            <stop offset="95%" stopColor="#0F626A" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorOrders" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#ffc107" stopOpacity={0.1}/>
                            <stop offset="95%" stopColor="#ffc107" stopOpacity={0}/>
                        </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                    <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: '#6c757d', fontSize: 11 }} dy={10} />
                    
                    {/* Left Axis: Sales */}
                    <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{ fill: '#0F626A', fontSize: 11 }} tickFormatter={formatCompactNumber} />
                    
                    {/* Right Axis: Orders */}
                    <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{ fill: '#ffc107', fontSize: 11 }} />
                    
                    <Tooltip 
                        contentStyle={{ backgroundColor: '#212529', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '0.85rem' }}
                        itemStyle={{ color: '#fff' }}
                        formatter={(value, name) => [name === 'sales' ? formatCurrency(value) : value, name === 'sales' ? 'Revenue' : 'Orders']}
                        labelStyle={{ color: '#adb5bd', marginBottom: '0.5rem' }}
                    />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: '0.8rem', paddingTop: '10px' }} />

                    <Area yAxisId="left" type="monotone" dataKey="sales" name="Sales" stroke="#0F626A" strokeWidth={2} fillOpacity={1} fill="url(#colorSales)" />
                    <Area yAxisId="right" type="monotone" dataKey="orders" name="Orders" stroke="#ffc107" strokeWidth={2} fillOpacity={1} fill="url(#colorOrders)" />
                </AreaChart>
            </ResponsiveContainer>
        </div>
    );
};

// --- 4. NEW: Horizontal Bar Chart (Leaderboard) ---
const AdminHorizontalBarChart = ({ data }) => {
    if (!data || data.length === 0) return <div className="text-muted text-center py-5">No data available</div>;

    // Convert and sort descending by value
    const chartData = data.map(item => ({
        name: item.name,
        value: parseFloat(item.value) || 0
    })).sort((a, b) => b.value - a.value).slice(0, 10); // Top 10

    return (
        <div style={{ width: '100%', height: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
                <BarChart
                    layout="vertical"
                    data={chartData}
                    margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
                >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
                    <XAxis type="number" hide />
                    <YAxis 
                        dataKey="name" 
                        type="category" 
                        width={100} 
                        axisLine={false} 
                        tickLine={false} 
                        tick={{ fill: '#333', fontSize: 11, fontWeight: 500 }} 
                    />
                    <Tooltip 
                        cursor={{ fill: 'transparent' }}
                        contentStyle={{ backgroundColor: '#212529', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '0.85rem' }}
                        formatter={(value) => [formatCurrency(value), 'Revenue']}
                        itemStyle={{ color: '#fff' }}
                    />
                    <Bar dataKey="value" fill="#0F626A" radius={[0, 4, 4, 0]} barSize={18} activeBar={{ fill: '#0a4b52' }} />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
};

const MilmaAdminUserDashboard = () => {
    const [dates, setDates] = useState({ start: new Date().toISOString().split('T')[0], end: new Date().toISOString().split('T')[0] });
    const [franchisees, setFranchisees] = useState([]);
    const [selectedFranchisees, setSelectedFranchisees] = useState([]);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchFranchisees = async () => {
            try {
                const res = await risapi.get('/api/milma-admin/franchisees-list', getAxiosConfig()); 
                if (res.data) setFranchisees(res.data.map(f => ({ value: f.id, label: f.name })));
            } catch (err) { console.error(err); }
        };
        fetchFranchisees();
    }, []);

    useEffect(() => {
        const fetchDashboardData = async () => {
            setLoading(true);
            try {
                const res = await risapi.get('/api/milma-admin/dashboard-summary', { 
                    params: { start_date: dates.start, end_date: dates.end, franchisee_ids: selectedFranchisees.map(f => f.value) }, 
                    ...getAxiosConfig() 
                });
                if (res.status === 200) setData(res.data);
            } catch (error) { console.error(error); } 
            finally { setLoading(false); }
        };
        fetchDashboardData();
    }, [dates, selectedFranchisees]);

    const getGradientStyle = (type) => {
        const base = { border: 'none', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', color: '#fff', overflow: 'hidden', position: 'relative' };
        switch (type) {
            case 'primary': return { ...base, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' };
            case 'info': return { ...base, background: 'linear-gradient(135deg, #0ba360 0%, #3cba92 100%)' };
            case 'success': return { ...base, background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' };
            default: return base;
        }
    };
    const iconCircleStyle = { backgroundColor: 'rgba(255, 255, 255, 0.2)', width: '45px', height: '45px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', backdropFilter: 'blur(5px)' };
    const tableHeaderStyle = { backgroundColor: '#f8f9fa', borderBottom: '1px solid #e9ecef', padding: '1.25rem 1.5rem', borderRadius: '12px 12px 0 0' };

    return (
        <main className="bg-light-subtle h-100">
            <div className="container-fluid pb-5">
                {/* Filters */}
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mt-4 mb-4 gap-3">
                    <div><h4 className="mb-1 text-dark fw-bold">Admin Overview</h4><p className="text-muted mb-0 f-s-13">Monitor franchisee performance.</p></div>
                    <div className="d-flex gap-2 bg-white p-2 rounded shadow-sm border align-items-center">
                        <input type="date" className="form-control form-control-sm border-0 bg-light" value={dates.start} onChange={e => setDates({...dates, start: e.target.value})} />
                        <span className="text-muted fw-bold">-</span>
                        <input type="date" className="form-control form-control-sm border-0 bg-light" value={dates.end} onChange={e => setDates({...dates, end: e.target.value})} />
                        <div style={{ minWidth: '250px' }}><Select options={franchisees} isMulti value={selectedFranchisees} onChange={setSelectedFranchisees} placeholder="All Franchisees..." styles={{ control: (base) => ({ ...base, minHeight: '32px', fontSize: '0.9rem', border: 'none', backgroundColor: '#f8f9fa' }) }} /></div>
                    </div>
                </div>

                {loading || !data ? <div className="loader_box"><div className="loader_14"></div></div> : (
                    <>
                        {/* Stats Cards */}
                        <div className="row g-3 mb-4">
                            <div className="col-md-4">
                                <div className="card h-100" style={getGradientStyle('primary')}>
                                    <div className="card-body p-4 d-flex justify-content-between align-items-center">
                                        <div><p className="mb-1 f-s-13 fw-500 opacity-75">Total Sales</p><h3 className="fw-bold mb-0">{formatCurrency(data.totals.sales)}</h3></div>
                                        <div style={iconCircleStyle}><i className="ti ti-currency-rupee" /></div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="card h-100" style={getGradientStyle('info')}>
                                    <div className="card-body p-4 d-flex justify-content-between align-items-center">
                                        <div><p className="mb-1 f-s-13 fw-500 opacity-75">Total Orders</p><h3 className="fw-bold mb-0">{data.totals.orders}</h3></div>
                                        <div style={iconCircleStyle}><i className="ti ti-shopping-cart" /></div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="card h-100" style={getGradientStyle('success')}>
                                    <div className="card-body p-4 d-flex justify-content-between align-items-center">
                                        <div><p className="mb-1 f-s-13 fw-500 opacity-75">Selected Franchisees</p><h3 className="fw-bold mb-0">{data.totals.franchisee_count}</h3></div>
                                        <div style={iconCircleStyle}><i className="ti ti-building-store" /></div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Row 2: Sales Trend & Revenue Share */}
                        <div className="row g-4 mb-4">
                            <div className="col-lg-8">
                                <div className="card border-0 shadow-sm h-100" style={{borderRadius: '12px'}}>
                                    <div style={tableHeaderStyle} className="d-flex justify-content-between align-items-center">
                                        <h6 className="fw-bold text-dark mb-0">Sales Trend <span className="text-muted fw-normal f-s-12 ms-2">({data.period_type})</span></h6>
                                        <span className="badge bg-white text-muted border fw-normal">Selected Period</span>
                                    </div>
                                    <div className="card-body px-4 pb-4">
                                        <AdminBarChart data={data.chart_data} />
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-4">
                                <div className="card border-0 shadow-sm h-100" style={{borderRadius: '12px'}}>
                                    <div style={tableHeaderStyle}>
                                        <h6 className="fw-bold text-dark mb-0">{data.mode === 'single' ? 'Top Products Share' : 'Franchisee Share'}</h6>
                                    </div>
                                    <div className="card-body px-2 pb-4 d-flex align-items-center justify-content-center">
                                        {/* Use Leaderboard data for both views (Products or Franchisees) */}
                                        <AdminPieChart data={data.leaderboard} nameKey="name" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Row 3: Revenue vs Orders & Leaderboard (Replaced Tables) */}
                        <div className="row g-4">
                            <div className="col-lg-8">
                                <div className="card border-0 shadow-sm h-100" style={{borderRadius: '12px'}}>
                                    <div style={tableHeaderStyle}>
                                        <h6 className="fw-bold text-dark mb-0">Revenue vs. Orders Analysis</h6>
                                    </div>
                                    <div className="card-body px-4 pb-4">
                                        <AdminAreaChart data={data.chart_data} />
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-4">
                                <div className="card border-0 shadow-sm h-100" style={{borderRadius: '12px'}}>
                                    <div style={tableHeaderStyle}>
                                        <h6 className="fw-bold text-dark mb-0">{data.mode === 'single' ? 'Top Selling Products' : 'Top Franchisees'}</h6>
                                    </div>
                                    <div className="card-body px-4 pb-4">
                                        <AdminHorizontalBarChart data={data.leaderboard} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </main>
    );
};

export default MilmaAdminUserDashboard;